import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { Autocomplete } from '@material-ui/lab';
import { TextField, Button, MenuItem, CircularProgress } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import ruLocale from 'date-fns/locale/ru';
import enLocale from 'date-fns/locale/en-US';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { actions as locationActions } from '../../../../store/ducks/yaLocations.duck';
import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';
import AutocompleteLocations from '../../../../components/AutocompleteLocations';
import PickupLocationSelect from '../../cart/components/PickupLocationSelect';
import { useStyles } from '../hooks/useStyles';
import { IAppState } from '../../../../store/rootDuck';
import { ILocation } from '../../../../interfaces/locations';
import { IOrderType } from '../../../../interfaces/order';
import { getStatusTypeWithTranslates } from '../constants';
import { IStore } from '../../../../interfaces/store';

const locales: { [key: string]: any } = {
  ru: ruLocale,
  en: enLocale,
};

export const OrderForm = ({
  formik,
  disabled,
  submitCallback,
  chosenCdekCity,
  setChosenCdekCity,
  shippingLocation,
  cdekCitiesToShow,
  setShippingLocation,
  orderTypes,
  stores,
}: {
  formik: any;
  disabled: boolean;
  submitCallback: () => void;
  chosenCdekCity: any;
  setChosenCdekCity: (city: any) => void;
  shippingLocation: ILocation | null;
  setShippingLocation: (loc: ILocation | null) => void;
  cdekCitiesToShow: any;
  orderTypes: IOrderType[];
  stores: IStore[];
}) => {
  const [selectedPoint, setSelectedPoint] = React.useState<any>({
    latitude: shippingLocation?.lat ?? 55.751244,
    longitude: shippingLocation?.lng ?? 37.618423,
  });
  const intl = useIntl();
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  const translatesStatus: { [type: string]: string } = {
    paid: intl.formatMessage({ id: 'ORDER.TYPE.PAYED' }),
    not_paid: intl.formatMessage({ id: 'ORDER.TYPE.NOT_PAID' }),
  };

  const orderStatusTypes = getStatusTypeWithTranslates(translatesStatus, true);

  const { order, editLoading } = useSelector(
    ({ orders }: IAppState) => ({
      order: orders.order,
      editLoading: orders.editLoading,
    }),
    shallowEqual
  );

  const { pointsCdek, loadingCdek, loadingCdekCities } = useSelector(
    ({ yaMap }: IAppState) => ({
      pointsCdek: yaMap.pickupPointsCdek,
      loadingCdek: yaMap.loadingCdek,
      loadingCdekCities: yaMap.loadingCdekCities,
    }),
    shallowEqual
  );

  const { loadingLocations, locations, byCoordsLoading } = useSelector(
    ({ yaLocations }: IAppState) => ({
      loadingLocations: yaLocations.loading,
      locations: yaLocations.yaLocations,
      byCoordsLoading: yaLocations.getLoacationByCoordsLoading,
    }),
    shallowEqual
  );

  const firstTenItems = pointsCdek?.data?.slice(0, 200);

    useEffect(() => {
    if (order?.delivery_type === 'self' && order?.point) {
      setSelectedPoint(order.point);
    }
  }, [order]);

  const { values, handleBlur, handleChange, errors, touched, setFieldValue } = formik;

  return (
    <>
      <TextField
        type='text'
        label={intl.formatMessage({ id: 'ORDER.INPUT.NAME' })}
        margin='normal'
        name='fio'
        value={values.fio}
        variant='outlined'
        helperText={touched.fio && errors.fio}
        error={Boolean(touched.fio && errors.fio)}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled}
      />
      <TextField
        type='text'
        label={intl.formatMessage({ id: 'ORDER.INPUT.PHONE' })}
        margin='normal'
        name='phone'
        value={values.phone}
        variant='outlined'
        helperText={touched.phone && errors.phone}
        error={Boolean(touched.phone && errors.phone)}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled}
      />
      <TextField
        type='email'
        label={intl.formatMessage({ id: 'USER.EDIT.EMAIL' })}
        margin='normal'
        name='email'
        value={values.email}
        variant='outlined'
        helperText={touched.email && errors.email}
        error={Boolean(touched.email && errors.email)}
        onBlur={handleBlur}
        onChange={handleChange}
        disabled={disabled}
      />
      <TextField
        select
        margin='normal'
        label='Вид доставки'
        value={values.deliveryType || 'delivery'}
        onChange={handleChange}
        name='deliveryType'
        variant='outlined'
        helperText={touched.deliveryType && errors.deliveryType}
        error={Boolean(touched.deliveryType && errors.deliveryType)}
        disabled={disabled}
      >
        <MenuItem value='delivery'>{intl.formatMessage({ id: 'SUBMENU.SHIPPING' })}</MenuItem>
        <MenuItem value='self'>
          {intl.formatMessage({ id: 'CART.DELIVERY_TYPE.SELF' })}
        </MenuItem>
      </TextField>

      <MuiPickersUtilsProvider locale={locales[intl.locale]} utils={DateFnsUtils}>
        <KeyboardDatePicker
          label={intl.formatMessage({ id: 'ORDER.DELIVERY.DATE' })}
          margin='normal'
          name='received_at'
          format='dd MMMM'
          value={values.receivedAtString}
          helperText={touched.receivedAtString && errors.receivedAtString}
          error={Boolean(touched.receivedAtString && errors.receivedAtString)}
          onBlur={handleBlur}
          onChange={date => {
            setFieldValue('receivedAtString', date);
          }}
          minDate={Date.now()}
          inputVariant='outlined'
          disabled={disabled}
        />
      </MuiPickersUtilsProvider>

      {order?.delivery_type === 'delivery' && order.store && (
        <TextField
          select
          label='Склад доставки'
          margin='normal'
          className='kt-width-full'
          variant='outlined'
          value={values?.store?.id || 0}
          onChange={e => {
            const store = stores.find(item => item.id === Number(e.target.value));
            setFieldValue('store', store);
          }}
          disabled={disabled}
        >
          {stores.map(item => (
            <MenuItem key={item.id} value={item.id || 0}>
              {item?.location?.address || ''}
            </MenuItem>
          ))}
        </TextField>
      )}

      {order?.delivery_type === 'delivery' && !order.store && (
        <AutocompleteLocations
          options={locations || []}
          loading={loadingLocations || byCoordsLoading}
          // @ts-ignore
          inputValue={{ address: values.address }}
          editable
          label={intl.formatMessage({
            id: 'STORE.INPUT.LOCATION',
          })}
          inputClassName={{ width: '100%' }}
          inputError={Boolean(touched.address && errors.address && errors.address)}
          inputHelperText={touched.address && errors.address && errors.address}
          fetchLocations={data => dispatch(locationActions.fetchRequest(data))}
          clearLocations={() => dispatch(locationActions.clear())}
          setSelectedLocation={location => {
            setShippingLocation(location || null);
            location
              ? setFieldValue('address', location.address)
              : setFieldValue('address', '');
          }}
          handleBlur={handleBlur}
          disable={disabled}
        />
      )}

      {order?.delivery_type !== 'delivery' && (
        <>
          <Autocomplete
            options={cdekCitiesToShow}
            autoHighlight
            value={chosenCdekCity?.label ?? values.addressCDEKCity ?? ''}
            loading={loadingCdekCities}
            onChange={(_, value) => {
              setChosenCdekCity(value);
              setSelectedPoint({
                latitude: value?.latitude,
                longitude: value?.longitude,
              });
            }}
            noOptionsText='Пусто'
            filterOptions={o => o}
            renderOption={(props, option: any) => {
              return (
                <li {...option} key={props.code}>
                  {props.full_name || props.label}
                </li>
              );
            }}
            disabled={disabled}
            renderInput={params => (
              <TextField
                {...params}
                type='text'
                margin='normal'
                name='location'
                variant='outlined'
                label={intl.formatMessage({
                  id: 'STORE.INPUT.CDEKLOCATION',
                })}
              />
            )}
          />

          {chosenCdekCity &&
            (!firstTenItems || loadingCdek || loadingCdekCities ? (
              <CircularProgress size={30} />
            ) : (
              <PickupLocationSelect
                value={selectedPoint}
                isLoading={loadingCdek}
                disabled={disabled}
                onChange={(store: any) => {
                  setSelectedPoint(store.location);
                  setFieldValue('location', store);
                  setFieldValue('pointCode', store.code);
                  setFieldValue('pointId', store.id);
                  setFieldValue('address', store.location.address);
                  setFieldValue('city', store.location.city);
                  localStorage.setItem('selectedStore', JSON.stringify(store));
                }}
                stores={firstTenItems}
                error={false}
                intl={intl}
              />
            ))}
        </>
      )}
      <TextField
        select
        margin='normal'
        label={`${intl.formatMessage({ id: 'ORDER.INPUT.STATUS' })}`}
        value={values.status || ''}
        onChange={handleChange}
        name='status'
        variant='outlined'
        helperText={touched.status && errors.status}
        error={Boolean(touched.status && errors.status)}
        disabled={disabled}
      >
        {orderTypes.map(item => (
          <MenuItem value={item.type}>{item.translate}</MenuItem>
        ))}
      </TextField>
      <TextField
        select
        margin='normal'
        label={`${intl.formatMessage({ id: 'ORDER.TABLE.STATUS.PAYMENT' })}`}
        value={values.payment_status || ''}
        onChange={handleChange}
        name='payment_status'
        variant='outlined'
        helperText={touched.payment_status && errors.payment_status}
        error={Boolean(touched.payment_status && errors.payment_status)}
        disabled={disabled}
      >
        {orderStatusTypes.map(item => (
          <MenuItem value={item.type}>{item.translate}</MenuItem>
        ))}
      </TextField>
      {!disabled && (
        <div className={classes.bottomActions}>
          <Button
            onClick={() => history.goBack()}
            className={classes.buttons}
            variant='outlined'
            color='primary'
          >
            {intl.formatMessage({ id: 'CATEGORY.BUTTON.CANCEL' })}
          </Button>
          <ButtonWithLoader
            disabled={editLoading}
            loading={editLoading}
            onPress={submitCallback}
          >
            {intl.formatMessage({ id: 'COMMON.BUTTON.SAVE' })}
          </ButtonWithLoader>
        </div>
      )}
    </>
  );
};
